import React, { CSSProperties, FormEvent, ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Check,
  ChevronDown,
  Grid3X3,
  Heart,
  List,
  Menu,
  Minus,
  Plus,
  Search,
  Shield,
  ShoppingBag,
  Sparkles,
  Truck,
  User,
  X,
  MapPin,
  Trash2,
} from "lucide-react";
import { AppConfigFields, ContactInfoFields } from "../../types/appConfigEntities";
import { userStorage } from "../../utils/storage";

export const Icon = {
  menu: (props: any) => <Menu size={16} strokeWidth={1.6} {...props} />,
  search: (props: any) => <Search size={16} strokeWidth={1.6} {...props} />,
  bag: (props: any) => <ShoppingBag size={16} strokeWidth={1.6} {...props} />,
  user: (props: any) => <User size={16} strokeWidth={1.6} {...props} />,
  heart: (props: any) => <Heart size={16} strokeWidth={1.6} {...props} />,
  arrowLeft: (props: any) => <ArrowLeft size={16} strokeWidth={1.6} {...props} />,
  arrowRight: (props: any) => <ArrowRight size={16} strokeWidth={1.6} {...props} />,
  arrowUpRight: (props: any) => <ArrowUpRight size={16} strokeWidth={1.6} {...props} />,
  plus: (props: any) => <Plus size={16} strokeWidth={1.6} {...props} />,
  minus: (props: any) => <Minus size={16} strokeWidth={1.6} {...props} />,
  close: (props: any) => <X size={16} strokeWidth={1.6} {...props} />,
  truck: (props: any) => <Truck size={16} strokeWidth={1.6} {...props} />,
  shield: (props: any) => <Shield size={16} strokeWidth={1.6} {...props} />,
  spark: (props: any) => <Sparkles size={14} strokeWidth={1.6} {...props} />,
  pin: (props: any) => <MapPin size={16} strokeWidth={1.6} {...props} />,
  check: (props: any) => <Check size={12} strokeWidth={3} {...props} />,
  chevron: (props: any) => <ChevronDown size={12} strokeWidth={2} {...props} />,
  grid: (props: any) => <Grid3X3 size={12} strokeWidth={2} {...props} />,
  list: (props: any) => <List size={12} strokeWidth={2} {...props} />,
  trash: (props: any) => <Trash2 size={14} strokeWidth={1.6} {...props} />,
};

export function MouchiMark({ size = 22, color = "currentColor" }: { size?: number; color?: string }) {
  return (
    <span
      style={{
        color,
        display: "inline-flex",
        alignItems: "center",
        fontSize: size,
        fontWeight: 600,
        letterSpacing: "0.32em",
        lineHeight: 1,
        textTransform: "uppercase",
      }}
    >
      Mouchi
    </span>
  );
}

export function PromoStrip() {
  const minAmount = window.APP_CONFIG?.[AppConfigFields.MinimumPurchaseAmount] ?? 150000;
  const discounts = window.APP_CONFIG?.[AppConfigFields.Discounts] ?? [
    { percent: "10% OFF", threshold: "$600.000" },
    { percent: "15% OFF", threshold: "$1.500.000" },
    { percent: "20% OFF", threshold: "$2.000.000" },
  ];

  return (
    <div
      style={{
        width: "100%",
        padding: "10px 24px",
        background: "rgba(10,10,10,0.92)",
        color: "var(--cream-100)",
        fontSize: 11.5,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        textAlign: "center",
        fontWeight: 500,
        position: "relative",
        zIndex: 30,
      }}
    >
      <span style={{ opacity: .7, marginRight: 14 }}>Compra minima</span>
      {`$${Number(minAmount).toLocaleString("es-AR")}`}
      {discounts.slice(0, 3).map((discount: any) => (
        <React.Fragment key={`${discount.percent}-${discount.threshold}`}>
          <span style={{ margin: "0 18px", opacity: .35 }}>.</span>
          {discount.percent} +{discount.threshold}
        </React.Fragment>
      ))}
    </div>
  );
}

type GlassNavProps = {
  active?: string;
  cartCount?: number;
  onMenuClick: () => void;
  onCartClick: () => void;
};

export function GlassNav({ active = "home", cartCount = 0, onMenuClick, onCartClick }: GlassNavProps) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const user = userStorage.get();

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const query = searchTerm.trim();
    if (query) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
      setSearchTerm("");
    }
  };

  const navItems = [
    ["Inicio", "home", "/"],
    ["Acero Dorado", "gold", "/categoria/acero-dorado"],
    ["Acero Blanco", "white", "/categoria/acero-quirurgico-blanco"],
    ["Hilo y Acero", "cord", "/categoria/hilo-y-acero"],
    ["Charms", "charms", "/categoria/pulseras-charms"],
    ["Novedades", "new", "/productos"],
  ];

  return (
    <div
      style={{
        position: "sticky",
        top: 16,
        zIndex: 25,
        display: "flex",
        justifyContent: "center",
        padding: "16px 24px 0",
        pointerEvents: "none",
      }}
    >
      <div
        className="lg-strong"
        style={{
          pointerEvents: "auto",
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          alignItems: "center",
          gap: 24,
          padding: "10px 14px 10px 22px",
          borderRadius: 999,
          width: "min(1280px, 100%)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button type="button" className="pill" style={{ padding: "8px 12px" }} onClick={onMenuClick}>
            <Icon.menu />
          </button>
          <span style={{ width: 1, height: 22, background: "rgba(0,0,0,0.08)" }} />
          <Link to="/" aria-label="Mouchi">
            <MouchiMark size={16} />
          </Link>
        </div>

        <nav
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 6,
            fontSize: 13.5,
            fontWeight: 500,
          }}
        >
          {navItems.map(([label, key, href]) => (
            <Link
              key={key}
              to={href}
              style={{
                padding: "10px 16px",
                borderRadius: 999,
                color: active === key ? "var(--ink-900)" : "var(--ink-500)",
                background: active === key ? "rgba(10,10,10,0.06)" : "transparent",
                textDecoration: "none",
                transition: "background .15s",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Link>
          ))}
        </nav>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <form className="pill" style={{ padding: "8px 14px", gap: 8 }} onSubmit={submitSearch}>
            <Icon.search />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar joyas..."
              style={{
                width: 118,
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--ink-900)",
                fontSize: 13,
                fontWeight: 400,
              }}
            />
          </form>
          <button type="button" className="pill" style={{ padding: 10, position: "relative" }} onClick={onCartClick}>
            <Icon.bag />
            {cartCount > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  minWidth: 18,
                  height: 18,
                  padding: "0 5px",
                  borderRadius: 999,
                  background: "var(--ink-900)",
                  color: "var(--cream-50)",
                  fontSize: 10,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px solid var(--cream-100)",
                }}
              >
                {cartCount}
              </span>
            )}
          </button>
          <button
            type="button"
            className="pill"
            style={{ padding: 10 }}
            onClick={() => navigate(user ? "/perfil" : "/login")}
          >
            <Icon.user />
          </button>
        </div>
      </div>
    </div>
  );
}

type ImgPhProps = {
  label?: string;
  ratio?: string;
  style?: CSSProperties;
  children?: ReactNode;
  src?: string;
  brightness?: number;
};

export function ImgPh({ label, ratio = "1/1", style = {}, children, src, brightness = 1 }: ImgPhProps) {
  return (
    <div
      className="img-ph"
      style={{
        aspectRatio: ratio,
        width: "100%",
        borderRadius: "inherit",
        position: "relative",
        overflow: "hidden",
        ...style,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={label || "imagen"}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            filter: `brightness(${brightness})`,
          }}
        />
      ) : (
        <span>{label || "imagen"}</span>
      )}
      {children}
    </div>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  action,
}: {
  eyebrow: string;
  title: string;
  action?: ReactNode;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "end", marginBottom: 28, padding: "0 8px" }}>
      <div>
        <div className="eyebrow" style={{ marginBottom: 10 }}>{eyebrow}</div>
        <h2 className="h-section" style={{ margin: 0 }}>{title}</h2>
      </div>
      {action}
    </div>
  );
}

export function GlassFooter({
  companyName = "Mouchi",
  year = new Date().getFullYear(),
}: {
  companyName?: string;
  year?: number;
}) {
  const contactInfo = window.APP_CONFIG?.[AppConfigFields.ContactData];
  const instagram = contactInfo?.[ContactInfoFields.Instagram] || "mouchi.oficial";
  const whatsapp = contactInfo?.[ContactInfoFields.Whatsapp] || "";
  const whatsappMessage = contactInfo?.[ContactInfoFields.WhatsappMessage] || "Hola como estas? Quiero solicitar informacion.";
  const phone = contactInfo?.[ContactInfoFields.Phone] || "";
  const email = contactInfo?.[ContactInfoFields.Email] || "";
  const address = contactInfo?.[ContactInfoFields.Address] || "";

  return (
    <footer style={{ padding: "80px 32px 32px", position: "relative", zIndex: 2 }}>
      <div
        className="lg-strong"
        style={{
          padding: "48px 56px 42px",
          borderRadius: 32,
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center" }}>
          <MouchiMark size={20} />
        </div>

        <nav style={{ display: "flex", justifyContent: "center", gap: 18, marginTop: 24, flexWrap: "wrap" }}>
          <a href="/" style={{ fontSize: 14, color: "var(--ink-700)", textDecoration: "none" }}>inicio</a>
          <a href="/productos" style={{ fontSize: 14, color: "var(--ink-700)", textDecoration: "none" }}>productos</a>
        </nav>

        <div style={{ display: "grid", gap: 12, marginTop: 30, justifyItems: "center", color: "var(--ink-700)", fontSize: 14 }}>
          <a
            href={`https://instagram.com/${instagram}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 10 }}
          >
            <Icon.spark /> @{instagram}
          </a>

          {whatsapp && (
            <a
              href={`https://wa.me/${whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(whatsappMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 10 }}
            >
              <Icon.bag /> {whatsapp}
            </a>
          )}

          {phone && <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}><Icon.user /> {phone}</span>}
          {email && <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}><Icon.search /> {email}</span>}
          {address && <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}><Icon.pin /> {address}</span>}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "var(--ink-400)",
          fontSize: 12,
          padding: "18px 8px 0",
        }}
      >
        <span>© Copyright {companyName} - {year}      |      Powered by ItapTec</span>
        <span>Todos los derechos reservados.</span>
      </div>
    </footer>
  );
}
